import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import hiveService from '../../services/hive';
import Tippy from 'tippy.js';
import 'tippy.js/themes/light.css';
import VotersModal from '../VotersModal/VotersModal';
import RichEditor from '../RichEditor';
import { htmlToMarkdown, markdownToHtml } from '../RichEditor/richEditorUtils';
import { postsApi } from '../../services/api';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import { DEFAULT_FEED_KEY, FEED_AUTHORS, FEED_DISPLAY_CONFIG, FEED_MAP, getDefaultEnabledFeedKeys } from '../../config/feed-details';
import { buildPostPath, buildFlexDetailPath } from '../../utils/postUrls';
import { useLists } from '../../context/ListsContext';
import AddToListModal from '../AddToListModal/AddToListModal';
import BookmarkModal from '../BookmarkModal/BookmarkModal';
import './PostActions.css';
import VoteSlider from '../VoteSlider/VoteSlider';
import usePostBlockchainData from '../../services/usePostBlockchainData';

const PostActions = ({ post, onCommentPosted, hideCommentButton = false, onReplyClick, showReply = false, onCommentToggle, commentsCount, loadingComments = false, mobileLimitPath = null }) => {
  const { user, isAuthenticated } = useAuth();
  const { isInFavourites, toggleFavourites, lists, loaded: listsLoaded } = useLists();
  const { addMutedUser, removeMutedUser, isUserMuted, showVoteSlider: globalShowVoteSlider, voteDefaultWeight, rewardDisplayMode, rewardDisplayCurrency } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const lastClickTimeRef = useRef({});
  const [isVoting, setIsVoting] = useState(false);
  const [showVoteSlider, setShowVoteSlider] = useState(false);
  const [voteWeight, setVoteWeight] = useState(voteDefaultWeight || 100);

  const {
    voteCount, setVoteCount,
    downvoteCount,
    replyCount,
    hasVoted, setHasVoted,
    userVoteWeight, setUserVoteWeight,
    pendingRewardHive, pendingRewardHbd,
    totalPayoutHive, totalPayoutHbd,
    cashoutTime,
    activeVotesWithValue,
    rewardsLoading,
    castVote
  } = usePostBlockchainData({
    post, user, isAuthenticated, rewardDisplayMode, rewardDisplayCurrency, initialReplyCount: commentsCount
  });

  // whenever the default weight updates, reset both the saved per-post weight
  // and the value we'll actually use for votes.
  useEffect(() => {
    setUserVoteWeight(null);
    setVoteWeight(voteDefaultWeight || 100);
  }, [voteDefaultWeight, setUserVoteWeight]);

  // keep local slider synced if the user manually changed their vote previously
  useEffect(() => {
    if (userVoteWeight != null) {
      setVoteWeight(userVoteWeight);
    }
  }, [userVoteWeight]);
  const [showFeedSelector, setShowFeedSelector] = useState(false);
  const [showArticleReblogConfirm, setShowArticleReblogConfirm] = useState(false);
  const [currentFeed, setCurrentFeed] = useState(null);
  const [selectedReblogFeed, setSelectedReblogFeed] = useState(null);
  const [enabledReblogFeedKeys, setEnabledReblogFeedKeys] = useState([]);
  const [reblogCommentary, setReblogCommentary] = useState('');
  const [reblogCommentaryHtml, setReblogCommentaryHtml] = useState('');
  const [isSubmittingReblog, setIsSubmittingReblog] = useState(false);
  const [showVotersModal, setShowVotersModal] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [favouritesPending, setFavouritesPending] = useState(false);
  const [showCopyNotice, setShowCopyNotice] = useState(false);
  const [copyNoticeMessage, setCopyNoticeMessage] = useState('Permlink copied');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editBody, setEditBody] = useState('');
  const [editBodyHtml, setEditBodyHtml] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [editFormat, setEditFormat] = useState('shortcode');
  const menuRef = useRef(null);
  const copyNoticeTimeoutRef = useRef(null);
  const rewardButtonRef = useRef(null);
  const rewardTippyRef = useRef(null);
  const reblogCommentaryEditorRef = useRef(null);
  const editEditorRef = useRef(null);
  useBodyScrollLock(showFeedSelector || showArticleReblogConfirm || showEditModal || showAddToListModal || showBookmarkModal);

  const fallbackEnabledReblogFeedKeys = useMemo(() => getDefaultEnabledFeedKeys().filter((key) => key !== 'global'), []);

  useEffect(() => {
    const fetchEnabledFeeds = async () => {
      try {
        const response = await fetch('/api/feed/feeds-config');
        const feeds = await response.json();
        if (!Array.isArray(feeds)) {
          throw new Error('Invalid feeds-config response');
        }

        const allowed = feeds
          .filter((feed) => feed?.enabled !== false)
          .map((feed) => String(feed.key || '').toLowerCase())
          .filter((key) => key && key !== 'global' && FEED_DISPLAY_CONFIG[key]);

        setEnabledReblogFeedKeys(allowed.length ? allowed : fallbackEnabledReblogFeedKeys);
      } catch (error) {
        console.error('Error fetching feeds config for reblog selector:', error);
        setEnabledReblogFeedKeys(fallbackEnabledReblogFeedKeys);
      }
    };

    fetchEnabledFeeds();
  }, [fallbackEnabledReblogFeedKeys]);

  const reblogFeedOptions = useMemo(
    () => enabledReblogFeedKeys.map((key) => ({ key, ...(FEED_DISPLAY_CONFIG[key] || {}) })),
    [enabledReblogFeedKeys]
  );

  const txId = useMemo(() => {
    if (!post) return null;
    if (post.tx_id) return post.tx_id;
    if (post.txId) return post.txId;

    const rawMetadata = post.json_metadata || post.metadata;
    if (!rawMetadata) return null;

    try {
      const metadata = typeof rawMetadata === 'string' ? JSON.parse(rawMetadata) : rawMetadata;
      return metadata?.tx_id || metadata?.txId || null;
    } catch {
      return null;
    }
  }, [post]);

  const handleReblogCommentaryChange = (html) => {
    setReblogCommentaryHtml(html);
    const markdown = htmlToMarkdown(html);
    setReblogCommentary(markdown);
  };

  const handleEditBodyChange = (html) => {
    setEditBodyHtml(html);
    setEditBody(htmlToMarkdown(html, editFormat));
  };

  // Cleanup on unmount
  useEffect(() => {
    // React 18 StrictMode runs effect cleanup during development re-mount checks.
    // Reset this flag on each mount pass so click handlers remain active.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (rewardTippyRef.current) {
        rewardTippyRef.current.destroy();
        rewardTippyRef.current = null;
      }
      if (copyNoticeTimeoutRef.current) {
        clearTimeout(copyNoticeTimeoutRef.current);
        copyNoticeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCopyLink = useCallback(async () => {
    const postUrl = `@${post.author}/${post.permlink}`;
    let copied = false;

    try {
      await navigator.clipboard.writeText(postUrl);
      copied = true;
    } catch (error) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = postUrl;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        copied = document.execCommand('copy');
        document.body.removeChild(textArea);
      } catch (fallbackError) {
        copied = false;
      }
    }

    if (!isMountedRef.current) return;

    setShowMenu(false);
    setCopyNoticeMessage(copied ? 'Permlink copied' : 'Unable to copy link');
    setShowCopyNotice(true);

    if (copyNoticeTimeoutRef.current) {
      clearTimeout(copyNoticeTimeoutRef.current);
    }

    copyNoticeTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setShowCopyNotice(false);
      }
      copyNoticeTimeoutRef.current = null;
    }, 1600);
  }, [post.author, post.permlink]);

  // Memoized handlers prevent re-registration on every render
  const handleClickOutside = useCallback((e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      if (isMountedRef.current) setShowMenu(false);
    }
  }, []);

  const handleKeydown = useCallback((e) => {
    if (e.key === 'Escape') {
      if (isMountedRef.current) setShowMenu(false);
    }
  }, []);

  // Register listeners only once via memoized handlers
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [handleClickOutside, handleKeydown]);

  // Handler to save scroll position and focus when clicking post link
  const handlePostLinkClick = () => {
    sessionStorage.setItem('scrollPosition', window.scrollY.toString());
    sessionStorage.setItem('returnPath', location.pathname);
    sessionStorage.setItem('preserveScroll', 'true');
    // mark the post to focus when returning from detail
    if (post && post.author && post.permlink) {
      sessionStorage.setItem('focusPost', `${post.author}/${post.permlink}`);
    }
  };

  // Define handleVote first before using it in handleVoteClick
  const handleVote = useCallback(async (isUpvote = true, explicitWeight = null) => {
    console.log('[handleVote] Starting vote', { isUpvote, explicitWeight, isMounted: isMountedRef.current });
    if (!isMountedRef.current) {
      console.log('[handleVote] Component not mounted, aborting');
      return;
    }
    
    if (!user?.username || !post?.author || !post?.permlink) {
      console.error('[handleVote] Missing required data', { user: user?.username, author: post?.author, permlink: post?.permlink });
      alert('Error: Missing post or user data');
      return;
    }
    
    setIsVoting(true);
    try {
      let percent = explicitWeight !== null ? explicitWeight : voteWeight;
      if (typeof percent !== 'number' || isNaN(percent)) {
        percent = voteDefaultWeight || 100;
      }
      console.log('[handleVote] Calling castVote', { percent, isUpvote });
      await castVote(percent, isUpvote);
      
      if (!isMountedRef.current) {
        console.log('[handleVote] Component unmounted after vote call');
        return;
      }
      setShowVoteSlider(false);
      setHasVoted(true);
      setVoteCount(prev => prev + 1);
      console.log('[handleVote] Vote successful!');
      alert('Vote submitted successfully! Weight: ' + percent + '%');
    } catch (error) {
      console.error('[handleVote] Vote error:', error);
      alert('Failed to vote: ' + error.message);
    } finally {
      if (isMountedRef.current) setIsVoting(false);
    }
  }, [voteWeight, voteDefaultWeight, user, post, castVote, setHasVoted, setVoteCount]);

  const handleVoteClick = useCallback(() => {
    const now = Date.now();
    if (lastClickTimeRef.current['vote'] && now - lastClickTimeRef.current['vote'] < 300) return;
    lastClickTimeRef.current['vote'] = now;

    if (!isAuthenticated) {
      if (isMountedRef.current) setShowAuthPrompt(true);
      return;
    }

    if (!isMountedRef.current) return;

    if (!globalShowVoteSlider) {
      const wt = (voteDefaultWeight || 100);
      handleVote(true, wt);
      return;
    }

    setVoteWeight(userVoteWeight != null ? userVoteWeight : (voteDefaultWeight || 100));
    setShowVoteSlider(prev => !prev);
  }, [isAuthenticated, globalShowVoteSlider, voteDefaultWeight, userVoteWeight, handleVote]);

  const formatRewardValue = (value) => {
    return value.toFixed(3);
  };

  const getDisplayReward = (reward) => {
    // If in author_only mode, show 50% (author's share)
    if (rewardDisplayMode === 'author_only' && reward > 0) {
      return reward * 0.5;
    }
    return reward;
  };

  const detectCurrentFeed = () => {
    return FEED_MAP[post.parentAuthor] || 'flex';
  };

  const getFeedDisplayName = (feedType) => {
    return FEED_DISPLAY_CONFIG[String(feedType || '').toLowerCase()]?.label || feedType;
  };

  const isArticlePost = (post?.depth === 0 || post?.depth === '0') && !post?.parentAuthor && !post?.parent_author;
  const isComment = (post?.depth > 0 || post?.depth === '1') && !isArticlePost;
  const rootAuthor = post?.rootAuthor || post?.parentAuthor || '';
  const isFlex = FEED_AUTHORS.includes(rootAuthor);

  // determine if the post belongs to the logged-in user (case‑insensitive)
  const isOwnPost = user && user.username && post && post.author &&
    user.username.toLowerCase() === post.author.toLowerCase();

  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
  const createdAtMs = post?.created ? new Date(post.created).getTime() : null;
  const isWithinEditWindow = Number.isFinite(createdAtMs)
    ? (Date.now() - createdAtMs) < SEVEN_DAYS_MS
    : false;

  const handleEditClick = () => {
    setShowMenu(false);

    if (isArticlePost) {
      navigate(`/create-article?edit=true&author=${encodeURIComponent(post.author)}&permlink=${encodeURIComponent(post.permlink)}`);
      return;
    }

    const existingBody = post?.body || '';
    
    let metaObj = {};
    if (typeof post?.json_metadata === 'string') {
      try { metaObj = JSON.parse(post.json_metadata); } catch {}
    } else {
      metaObj = post?.json_metadata || {};
    }
    setEditFormat(metaObj.shortcode ? 'shortcode' : 'markdown');
    
    setEditBody(existingBody);
    setEditBodyHtml(existingBody);
    setEditError('');
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!isOwnPost) return;
    if (!isWithinEditWindow) {
      setEditError('This post can no longer be edited after 7 days.');
      return;
    }
    if (!editBody.trim()) {
      setEditError('Post body cannot be empty.');
      return;
    }

    setEditError('');
    setIsSavingEdit(true);
    try {
      let metadata = post?.json_metadata || {};
      if (typeof metadata === 'string') {
        try {
          metadata = JSON.parse(metadata);
        } catch {
          metadata = {};
        }
      }

      await hiveService.editPost(
        post.author,
        post.permlink,
        post.title || '',
        editBody,
        post.parent_author || post.parentAuthor || '',
        post.parent_permlink || post.parentPermlink || '',
        metadata
      );

      try {
        await postsApi.upsertOptimisticShortPost({
          author: post.author,
          permlink: post.permlink,
          body: editBody,
          json_metadata: metadata,
          parent_author: post.parent_author || post.parentAuthor || '',
          parent_permlink: post.parent_permlink || post.parentPermlink || '',
          created_at: post.created_at || post.created || new Date().toISOString()
        });
      } catch (err) {
        console.warn('Failed to optimistically update post', err);
      }

      setShowEditModal(false);
      alert('Post updated successfully');
      if (typeof onCommentPosted === 'function') {
        onCommentPosted();
      } else {
        window.location.reload();
      }
    } catch (error) {
      setEditError(error.message || 'Failed to update post');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleReblogToOtherFeed = () => {
    if (!isAuthenticated) {
      alert('Please login to reblog');
      return;
    }

    // Long-form articles should be reblogged to the user's blog directly,
    // not wrapped as feed comments.
    if (isArticlePost) {
      setShowArticleReblogConfirm(true);
      return;
    }

    const feed = detectCurrentFeed();
    setCurrentFeed(feed);
    setSelectedReblogFeed(feed);
    setReblogCommentary('');
    setShowFeedSelector(true);
  };

  const handleConfirmArticleReblog = async () => {
    setShowArticleReblogConfirm(false);

    if (!user?.username) {
      alert('You must be logged in to reblog this article.');
      return;
    }

    try {
      await hiveService.reblog(user.username, post.author, post.permlink);
      alert('Article reblogged successfully!');
    } catch (error) {
      console.error('Reblog error:', error);
      alert('Failed to reblog: ' + error.message);
    }
  };

  const handleFeedSelect = (feedType) => {
    setSelectedReblogFeed(feedType);
  };

  useEffect(() => {
    if (!showFeedSelector || enabledReblogFeedKeys.length === 0) return;

    const fallback = enabledReblogFeedKeys.includes(DEFAULT_FEED_KEY)
      ? DEFAULT_FEED_KEY
      : enabledReblogFeedKeys[0];

    if (!selectedReblogFeed || !enabledReblogFeedKeys.includes(selectedReblogFeed)) {
      setSelectedReblogFeed(currentFeed && enabledReblogFeedKeys.includes(currentFeed) ? currentFeed : fallback);
    }
  }, [currentFeed, enabledReblogFeedKeys, selectedReblogFeed, showFeedSelector]);

  const handleConfirmFeedReblog = async () => {
    if (!selectedReblogFeed) {
      return;
    }

    if (!user?.username) {
      alert('You must be logged in to reblog this post.');
      return;
    }

    setIsSubmittingReblog(true);

    try {
      await hiveService.reblogAsComment(
        user.username, 
        post.author, 
        post.permlink,
        post.body,
        selectedReblogFeed,
        { commentary: reblogCommentary }
      );
      setShowFeedSelector(false);
      setReblogCommentary('');
      setReblogCommentaryHtml('');
      alert(`Post reblogged to ${getFeedDisplayName(selectedReblogFeed)} successfully!`);
    } catch (error) {
      console.error('Reblog error:', error);
      alert('Failed to reblog: ' + error.message);
    } finally {
      setIsSubmittingReblog(false);
    }
  };

  const handleVoteCountClick = useCallback((e) => {
    console.log('[VoteCountClick] Clicked with voteCount =', voteCount);
    if (voteCount > 0) {
      console.log('[VoteCountClick] Vote count > 0, opening voters modal');
      e.preventDefault();
      e.stopPropagation();
      if (isMountedRef.current) {
        console.log('[VoteCountClick] Setting showVotersModal to true');
        setShowVotersModal(true);
      }
    } else {
      console.log('[VoteCountClick] Vote count is 0, not opening modal');
    }
  }, [voteCount]);

  const rewardBaseValue = rewardDisplayCurrency === 'hbd' ? pendingRewardHbd : pendingRewardHive;
  const rewardDisplayValue = getDisplayReward(rewardBaseValue);
  const rewardUnit = rewardDisplayCurrency === 'hbd' ? 'HBD' : 'HIVE';
  
  // Determine reward title
  const getRewardTitle = () => {
    if (rewardBaseValue > 0) {
      return ''; // Tippy tooltip will handle this
    }
    // Check if post has been paid out
    const totalPaidOut = rewardDisplayCurrency === 'hbd' ? totalPayoutHbd : totalPayoutHive;
    if (totalPaidOut > 0) {
      const displayTotal = getDisplayReward(totalPaidOut);
      return `Paid out: ${formatRewardValue(displayTotal)} ${rewardUnit}`;
    }
    return 'No pending rewards';
  };

  useEffect(() => {
    if (!rewardButtonRef.current) return;

    const formatPayoutTime = () => {
      if (!cashoutTime) return 'N/A';
      const now = new Date();
      const diffMs = cashoutTime - now;
      
      if (diffMs < 0) return 'Paid out';
      
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      
      if (diffDays > 0) return `${diffDays}d ${diffHours}h remaining`;
      if (diffHours > 0) return `${diffHours}h ${diffMins}m remaining`;
      return `${diffMins}m remaining`;
    };

    const formatRewardValueLocal = (value) => {
      return value.toFixed(3);
    };

    // Only update Tippy if the content actually changed
    const newContent = rewardBaseValue > 0 
      ? `<div class="reward-tooltip"><span class="strong"><strong>Pending Payout</strong><br/><span class="reward-display">${rewardDisplayMode === 'author_only' ? 'Author\'s share' : 'Total Reward'}</span></span><br/><span class="reward-value">${rewardDisplayValue > 0 ? formatRewardValueLocal(rewardDisplayValue) : '0'} ${rewardUnit}</span><br/>${formatPayoutTime()}</div>`
      : null;

    // Clear old Tippy instance if content disappeared
    if (!newContent && rewardTippyRef.current) {
      rewardTippyRef.current.destroy();
      rewardTippyRef.current = null;
      return;
    }

    // Create new Tippy only if we have content and haven't already created one
    if (newContent && !rewardTippyRef.current) {
      rewardTippyRef.current = Tippy(rewardButtonRef.current, {
        content: newContent,
        allowHTML: true,
        theme: 'light',
        arrow: true,
        delay: [500, 0]
      });
    } else if (newContent && rewardTippyRef.current) {
      // Update existing Tippy content if it exists
      try {
        rewardTippyRef.current.setContent(newContent);
      } catch (e) {
        // If update fails, recreate
        rewardTippyRef.current.destroy();
        rewardTippyRef.current = Tippy(rewardButtonRef.current, {
          content: newContent,
          allowHTML: true,
          theme: 'light',
          arrow: true,
          delay: [500, 0]
        });
      }
    }
  }, [rewardBaseValue, rewardDisplayValue, rewardDisplayMode, rewardUnit, cashoutTime]);

  return (
    <>
      <div className="post-actions">
        {!hideCommentButton && (
          <button title="Expand/Show comments" className={`action-btn ${loadingComments ? 'loading' : ''}`} disabled={!!(mobileLimitPath && window.innerWidth <= 768)} onClick={() => { onCommentToggle && onCommentToggle(); }} style={{cursor: 'pointer'}}>
            {loadingComments ? (
              <svg className="spinner-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" opacity="0.25" />
                <path d="M12 2 A10 10 0 0 1 22 12" strokeLinecap="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
            )}
            <span>{replyCount !== undefined ? replyCount : (commentsCount !== undefined ? commentsCount : (Array.isArray(post.replies) ? post.replies.length : (post.replies || 0)))}</span>
          </button>
        )}

        <button 
          className="action-btn"
          onClick={() => onReplyClick && onReplyClick()}
          title="Reply"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 10h10a8 8 0 0 1 8 8v2M3 10l6 6m-6-6 6-6" />
          </svg>
        </button>

        <button 
          className={`action-btn like-btn ${hasVoted ? 'voted' : ''}`}
          onClick={handleVoteClick}
          title="Vote"
        >
          <svg viewBox="0 0 24 24" fill={hasVoted ? "var(--accent-primary)" : "none"} stroke={hasVoted ? "var(--accent-primary)" : "currentColor"} strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          <span 
            className={voteCount > 0 ? 'clickable' : ''} 
            onClick={handleVoteCountClick}
            style={{ cursor: voteCount > 0 ? 'pointer' : 'default' }}
          >
            {voteCount}
          </span>
        </button>

        {downvoteCount > 0 && (
          <span
            className="downvote-indicator"
            title={`${downvoteCount} downvote${downvoteCount !== 1 ? 's' : ''}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowVotersModal(true); }}
            style={{ cursor: 'pointer' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              <line x1="4" y1="4" x2="20" y2="20" />
            </svg>
            <span>{downvoteCount}</span>
          </span>
        )}

        {!isComment && (
          <button 
            className="action-btn"
            onClick={() => {
              const now = Date.now();
              if (lastClickTimeRef.current['reblog'] && now - lastClickTimeRef.current['reblog'] < 300) return;
              lastClickTimeRef.current['reblog'] = now;
              handleReblogToOtherFeed();
            }}
            title="Repost"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="17 1 21 5 17 9" />
              <path d="M3 11V9a4 4 0 0 1 4-4h14" />
              <polyline points="7 23 3 19 7 15" />
              <path d="M21 13v2a4 4 0 0 1-4 4H3" />
            </svg>
          </button>
        )}

        <Link 
          to={isFlex ? buildFlexDetailPath(post.author, post.permlink) : buildPostPath(post.author, post.permlink)}
          className="action-btn action-link"
          title={isFlex ? "View Post" : "View Article"}
          onClick={handlePostLinkClick}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Link>

        <div className="rsm">
        <div className="action-dropdown-wrapper" ref={menuRef}>
          <button
            className="action-btn dropdown-toggle"
            onClick={() => {
              const now = Date.now();
              if (lastClickTimeRef.current['menu'] && now - lastClickTimeRef.current['menu'] < 300) return;
              lastClickTimeRef.current['menu'] = now;
              setShowMenu(prev => !prev);
            }}
            title="More actions"
            aria-haspopup="true"
            aria-expanded={showMenu}
          >
            ⋯
          </button>
          {showMenu && (
            <div className="action-dropdown-menu">
              {isOwnPost && isWithinEditWindow && (
                <button
                  className="dropdown-item"
                  onClick={handleEditClick}
                >
                  Edit
                </button>
              )}
              {isOwnPost && !isWithinEditWindow && (
                <button
                  className="dropdown-item dropdown-item-disabled"
                  type="button"
                  disabled
                  title="Editing window has expired"
                >
                  Edit
                </button>
              )}
              {user?.username && !isOwnPost && listsLoaded && (
                <button
                  className="dropdown-item"
                  disabled={favouritesPending}
                  onClick={async () => {
                    setFavouritesPending(true);
                    try { await toggleFavourites(post.author); } catch (e) {}
                    setFavouritesPending(false);
                  }}
                >
                  {isInFavourites(post.author) ? '★ Remove from favourites' : '☆ Add to favourites'}
                </button>
              )}
              {user?.username && !isOwnPost && listsLoaded && lists.length > 0 && (
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowMenu(false);
                    setShowAddToListModal(true);
                  }}
                >
                  ☰ Add to list
                </button>
              )}
              {user?.username && (
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowMenu(false);
                    setShowBookmarkModal(true);
                  }}
                >
                  🔖 Bookmark
                </button>
              )}
              {user?.username && !isOwnPost && (
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setShowMenu(false);
                    if (isUserMuted(post.author)) {
                      if (window.confirm(`Unmute @${post.author}?`)) {
                        removeMutedUser(post.author);
                      }
                    } else {
                      if (window.confirm(`Are you sure you want to mute user\n@${post.author}?`)) {
                        addMutedUser(post.author);
                      }
                    }
                  }}
                >
                  {isUserMuted(post.author) ? 'Unmute user' : 'Mute user'}
                </button>
              )}
              <Link
                to={`/user/${post.author}`}
                className="dropdown-item"
                onClick={() => setShowMenu(false)}
              >
                View profile
              </Link>
              <button className="copy-link-btn dropdown-item" onClick={handleCopyLink}>
                Copy link
              </button>
              {txId && (
                <a
                  href={`https://hivexplorer.com/tx/${txId}`}
                  className="dropdown-item"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open on Hivexplorer"
                  onClick={() => setShowMenu(false)}
                >
                  Show Tx ID
                </a>
              )}
            </div>
          )}
          {showCopyNotice && (
            <div className="copy-link-notice" role="status" aria-live="polite">
              {copyNoticeMessage}
            </div>
          )}
        </div>

        {/* Reward Value Button with Tippy Tooltip */}
        <button 
          className={`action-btn reward-btn ${rewardsLoading ? 'loading' : ''}`}
          onClick={() => setShowVotersModal(true)}
          ref={rewardButtonRef}
          title={getRewardTitle()}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.5 10c.83 0 1.5-.67 1.5-1.5S16.33 9 15.5 9 14 9.67 14 10.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 9 8.5 9 7 9.67 7 10.5 7.67 12 8.5 12zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
          </svg>
          {rewardsLoading ? (
            <span className="loading-spinner"></span>
          ) : (
            rewardBaseValue > 0 && <span>{formatRewardValue(rewardDisplayValue)} {rewardUnit}</span>
          )}
        </button>
        </div>

        {showFeedSelector && (
          <div className="modal-overlay" onClick={() => setShowFeedSelector(false)}>
            <div className="modal-content feed-selector-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Select a feed to reblog to:</h3>
            
            <div className="feed-options">
              {reblogFeedOptions.map((feed) => (
                <button
                  key={feed.key}
                  type="button"
                  onClick={() => handleFeedSelect(feed.key)}
                  className={`feed-option-btn ${selectedReblogFeed === feed.key ? 'current-feed' : ''}`}
                >
                  <span className="feed-icon">{feed.icon}</span>
                  <span className="feed-name">{feed.label}</span>
                  {currentFeed === feed.key && <span className="current-badge">Source</span>}
                </button>
              ))}
            </div>
            <label className="reblog-commentary-label" htmlFor="reblog-commentary-input">Reflex with text:</label>
            <p style={{ marginBottom: '8px', fontSize: '11px' }}>Will be displayed above the original content</p>
            <RichEditor
              ref={reblogCommentaryEditorRef}
              value={reblogCommentaryHtml}
              onChange={handleReblogCommentaryChange}
              onSubmit={handleConfirmFeedReblog}
              placeholder="Add your own text"
              maxLength={280}
              toolbar={true}
            />
            <div className="slider-actions reflex-actions">
              <button
                onClick={() => {
                  setShowFeedSelector(false);
                  setReblogCommentary('');
                  setReblogCommentaryHtml('');
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmFeedReblog}
                className="confirm-btn"
                disabled={!selectedReblogFeed || isSubmittingReblog}
                title="Reflex is the new 'Reblog'"
              >
                {isSubmittingReblog
                  ? 'Posting...'
                  : (selectedReblogFeed || 'flex') === 'flex'
                    ? 'Reflex'
                    : `Reflex to ${getFeedDisplayName(selectedReblogFeed || 'flex').toLowerCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showArticleReblogConfirm && (
        <div className="modal-overlay" onClick={() => setShowArticleReblogConfirm(false)}>
          <div className="modal-content feed-selector-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reblog this article?</h3>
            <p>This will reblog directly to your articles/blog.</p>
            <div className="slider-actions">
              <button onClick={() => setShowArticleReblogConfirm(false)} className="cancel-btn">Cancel</button>
              <button onClick={handleConfirmArticleReblog} className="confirm-btn">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content feed-selector-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Post</h3>
            <p style={{ marginBottom: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              You can edit this post body until 7 days after publishing.
            </p>
            <RichEditor
              ref={editEditorRef}
              value={editBodyHtml}
              onChange={handleEditBodyChange}
              onSubmit={handleSaveEdit}
              placeholder="Edit your post..."
              maxLength={null}
              toolbar={true}
              autoFocus={true}
              defaultMode={editFormat === 'markdown' ? 'text' : 'editor'}
              outputFormat={editFormat}
            />
            {editError && <div className="error-message" style={{ marginTop: '8px' }}>{editError}</div>}
            <div className="slider-actions reflex-actions" style={{ marginTop: '10px' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditError('');
                }}
                className="cancel-btn"
                disabled={isSavingEdit}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="confirm-btn"
                disabled={isSavingEdit || !editBody.trim()}
              >
                {isSavingEdit ? 'Saving...' : 'Save Edit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

    {showVoteSlider && (
      <VoteSlider
        voteWeight={voteWeight}
        setVoteWeight={setVoteWeight}
        onCancel={() => setShowVoteSlider(false)}
        onVote={handleVote}
        isVoting={isVoting}
        showDownvote={false}
        showDebug={false}
        debugData={{ voter: user?.username, author: post.author, permlink: post.permlink }}
        userHasVoted={hasVoted}
        userVoteWeight={userVoteWeight}
      />
    )}

    {showAuthPrompt && (
      <div className="vote-slider-backdrop" onClick={() => setShowAuthPrompt(false)}>
        <div className="vote-slider-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="vote-slider-modal-inner" style={{ textAlign: 'center', padding: '24px' }}>
            <p style={{ marginBottom: '16px', fontSize: '1rem' }}>You need to be logged in to vote.</p>
            <button className="vote-cancel-btn" onClick={() => setShowAuthPrompt(false)}>Close</button>
          </div>
        </div>
      </div>
    )}

    {showVotersModal && (
      <VotersModal 
        author={post.author} 
        permlink={post.permlink}
        votesWithRewards={activeVotesWithValue}
        onClose={() => setShowVotersModal(false)} 
      />
    )}
    {showAddToListModal && (
      <AddToListModal
        targetUsername={post.author}
        onClose={() => setShowAddToListModal(false)}
      />
    )}
    {showBookmarkModal && (
      <BookmarkModal
        author={post.author}
        permlink={post.permlink}
        onClose={() => setShowBookmarkModal(false)}
      />
    )}
    </>
  );
};

export default React.memo(PostActions);
